// サンプル請求・お問い合わせのフォーム（11 店のフリーページ。shared/contact-pages.ts の飛ばし先）。wuSiteResource2 の
// Shared/EcSiteForm.ts（AngularJS 1.7 の EcSiteFormApp / MainCtrl）の引き取り先（Issue #106）。送信先も OppSites の
// `POST api.workup-system.com/api/Form` から wuStorefront の `POST /api/sample-form` へ移した（src/server/sample-form/）。
//
// ページ本文はフォームの HTML に data-wu-* の目印を付けたもの（<div id="sample-form" data-wu-app="sample-form">）。
// 11 店とも 10/29〜10/31 に旧の AngularJS の目印（ng-* と {{ }}）から書き換えた（Issue #123・#130・ChatBan #345）。
// ng-* だけのページでは何もしない（箱として見つからない）。汎用のテンプレートは作らず、11 本で使っている目印だけを扱う（#105 の 2-3）:
//   - data-wu-model  … name, company, postcode, address, mail, mail_confirm, phone, purpose, body, delivery, arrive, sample
//                      と sampleTextType（閲覧履歴の切り替え）。text / textarea / select / radio。確認画面の readonly の textarea も同じ値で埋める
//   - data-wu-show   … pageMode == / != 'Edit' 'Confirm' 'Sending' 'Complete'・delivery == / != 'none' 'free' 'pay'・
//                      isSendedError・submited && !validate()（evalShow が読める形だけ。toNgShow で c. 付きに直して渡す）
//   - data-wu-click / data-wu-change … toConfirm / toEdit / send / zipToAddress / addSample
//   - data-wu-repeat … エラーの一覧（errorMessages）だけ。中の data-wu-text="errorMessage" に 1 件ずつ入れる
//   - data-wu-text   … 中身を項目の値で描き直す・data-wu-value … value を項目の値に（hidden の refurl）
// ★店ごとのずれ（無料サンプルの選択肢・用途欄・閲覧履歴ボタン）は、ページに要素があればそのまま動く。
// ★閲覧履歴から入力（旧は api.workup-system.com/api/Product を叩いた）は、ボタン（sampleTextType の radio の label・
//   addSample の要素）を**隠したまま**、「無料」「有料」を選んだときに空の請求サンプルの欄へ入れる形にした（Issue #107・10/21）。
//   口は GET /api/sample-form/history-products（src/server/sample-form/history.ts）。対象は shared の HISTORY_SITES（8 店）。
// ★旧との違い: 送信の失敗を見せて押し直せる（旧は catch が無く「送信しています」のまま止まった）・
//   入力の再開は Cookie sampleForm（店のページを開くたびにおちゃのこへ送られていた）をやめて localStorage（送信後に消す・30 日まで）・
//   「戻る」は旧の意図どおり（確認 → 戻るで入力へ・完了後は戻れない案内）。旧は最初の 1 回目の「戻る」で state が null になり例外で何もしなかった。
// ★旧 EcSiteForm.js がまだ読まれているページ（angular.module('EcSiteFormApp') が引ける）では何もしない。
//   （11 店とも script は外してある。目印とは別の守りとして残す）
//   店の CSS に残っている .ng-cloak { display:none } はどの要素にも当たらない（ページから class を外してある）。
// ★プレビュー: ?wu_sample_form_preview=1 は送信しても fetch しない（=fail で送信失敗の表示）。本番のフォームから試しに送らない
//   （本物の控えメールと、サンプル発送の段取りに乗る）。
// ★名前・住所・メール・電話・本文をログに出さない。

import { resolveDevApiOverride } from '../shared/dev-api-override'
import {
  CLIENT_ERROR_PATH,
  HISTORY_IDS_MAX,
  HISTORY_ITEMS_MAX,
  HISTORY_SITES,
  SAMPLE_FORM_APP_SELECTOR,
  SAMPLE_FORM_SITES,
  type ClientErrorReport,
  type DeliveryName,
  type SampleFormRequest,
} from '../shared/sample-form'
import { PRODUCT_LOG_COOKIE, readCookie } from './product-log'

export const SAMPLE_FORM_API = 'https://front.workup-system.com/api/sample-form'
export const ZIP_API = 'https://front.workup-system.com/api/zip'
export const APP_SELECTOR = SAMPLE_FORM_APP_SELECTOR

export const TEXT = {
  sendFailed: '送信できませんでした。時間をおいてもう一度お試しいただくか、info@workup.jp までご連絡ください',
  // 郵便番号の 2 つは旧（EcSiteForm.ts の zipToAddress）の原文ママ
  zipInvalid: '郵便番号が正しくありません',
  zipFailed: '住所の検索に失敗しました。お手数ですが住所を直接ご入力ください',
}

/** 旧 EcSiteForm.js（AngularJS のモジュール EcSiteFormApp）が同じページに居るか */
export function hasOldEcSiteFormApp(win: { angular?: { module?: (name: string) => unknown } }): boolean {
  const ng = win.angular
  if (!ng || typeof ng.module !== 'function') return false
  try {
    ng.module('EcSiteFormApp')
    return true
  } catch {
    return false // 未登録なら angular が投げる
  }
}

// ---- 入力の値 ----------------------------------------------------------------

/** 画面の入力（旧の formData と同じ名前。postcode / phone は送るときに postCode / phoneNumber へ写す） */
export interface FormData {
  name: string
  company: string
  postcode: string
  address: string
  mail: string
  mail_confirm: string
  phone: string
  purpose: string
  body: string
  delivery: string
  arrive: string
  sample: string
  refUrl: string
}

/** 入力の再開で残す項目（refUrl は開くたびに document.referrer で入れ直すので残さない） */
export const DRAFT_FIELDS = [
  'name',
  'company',
  'postcode',
  'address',
  'mail',
  'mail_confirm',
  'phone',
  'purpose',
  'body',
  'delivery',
  'arrive',
  'sample',
] as const satisfies readonly (keyof FormData)[]
export type Draft = Partial<Pick<FormData, (typeof DRAFT_FIELDS)[number]>>

/** 旧の初期値（delivery は none・時間帯は「指定なし（最短）」） */
export function defaultFormData(referrer: string): FormData {
  return {
    name: '',
    company: '',
    postcode: '',
    address: '',
    mail: '',
    mail_confirm: '',
    phone: '',
    purpose: '',
    body: '',
    delivery: 'none',
    arrive: '指定なし（最短）',
    sample: '',
    refUrl: referrer,
  }
}

const empty = (s: string | undefined): boolean => s === undefined || s === ''

/** 旧の validate() と同じ順・同じ文言 */
export function validate(d: FormData): string[] {
  const m: string[] = []
  if (empty(d.name)) m.push('お名前を入力してください')
  if (empty(d.postcode)) m.push('郵便番号を入力してください')
  if (empty(d.address)) m.push('ご住所を入力してください')
  if (empty(d.phone)) m.push('電話番号を入力してください')
  if (empty(d.mail)) m.push('メールアドレスを入力してください')
  if (d.mail !== d.mail_confirm) m.push('メールアドレスと確認用が一致していません')
  if (d.delivery === 'none') {
    if (empty(d.body)) m.push('お問い合わせを入力してください')
  } else if (empty(d.sample)) {
    m.push('請求サンプルを入力してください')
  }
  return m
}

const DELIVERIES: DeliveryName[] = ['none', 'free', 'pay']

/** 送る形。受ける項目は API と同じ決め打ち（mail_confirm は送らない） */
export function toRequest(d: FormData, siteInfoId: number, website: string): SampleFormRequest {
  return {
    siteInfoId,
    name: d.name,
    company: d.company,
    postCode: d.postcode,
    address: d.address,
    mail: d.mail,
    phoneNumber: d.phone,
    purpose: d.purpose,
    body: d.body,
    delivery: DELIVERIES.includes(d.delivery as DeliveryName) ? (d.delivery as DeliveryName) : 'none',
    arrive: d.arrive,
    sample: d.sample,
    refUrl: d.refUrl,
    website,
  }
}

export type SendResult = 'ok' | 'failed'

/** POST。200 → ok / それ以外・通信エラー → failed（投げない） */
/** 失敗したら report に理由（http-NNN / network）を渡す（ChatBan #332。店と理由だけを口へ知らせる） */
export async function postForm(
  api: string,
  req: SampleFormRequest,
  fetchFn: typeof fetch = fetch,
  report?: (reason: string) => void,
): Promise<SendResult> {
  try {
    const r = await fetchFn(api, {
      method: 'POST',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    })
    if (r.ok) return 'ok'
    report?.(`http-${r.status}`)
    return 'failed'
  } catch {
    report?.('network')
    return 'failed'
  }
}

/**
 * 送信の失敗を口に知らせる（店と理由だけ。入力の中身は送らない）。text/plain にしてプリフライトを飛ばさない。
 * 知らせ自体の失敗は無視する（客の画面には何も出さない）
 */
export function reportClientError(api: string, siteInfoId: number, reason: string, fetchFn: typeof fetch = fetch): void {
  const report: ClientErrorReport = { siteInfoId, reason }
  try {
    void fetchFn(`${api}${CLIENT_ERROR_PATH}`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify(report),
    }).catch(() => undefined)
  } catch {
    // fetch が無い・同期で投げる環境
  }
}

export type ZipResult = { ok: true; address: string } | { ok: false; message: string }

/** 郵便番号 → 住所（旧と同じ /api/zip。400 / 404 は郵便番号の誤り、それ以外は検索の失敗） */
export async function lookupZip(api: string, zipcode: string, fetchFn: typeof fetch = fetch): Promise<ZipResult> {
  try {
    const r = await fetchFn(`${api}?zipcode=${encodeURIComponent(zipcode)}`, { cache: 'no-store' })
    if (r.status === 400 || r.status === 404) return { ok: false, message: TEXT.zipInvalid }
    if (!r.ok) return { ok: false, message: TEXT.zipFailed }
    const j = (await r.json()) as { fullAddress?: unknown }
    return typeof j.fullAddress === 'string' ? { ok: true, address: j.fullAddress } : { ok: false, message: TEXT.zipFailed }
  } catch {
    return { ok: false, message: TEXT.zipFailed }
  }
}

// ---- 閲覧履歴から請求サンプルを入れる（Issue #107） ------------------------------
// 旧は「ご自分で入力 / 閲覧履歴から入力」の radio で欄を差し替えていた（「履歴がありません」も入れた）。
// 新は radio を隠したまま、「無料」「有料」を選んだときに欄が空なら入れるだけ（10/21 田口さん）。
//   - 開いたときは何もしない（初期値の「不要」も変えない）
//   - 欄に何か書いてあれば上書きしない（選び直したときも、返事を待つ間に書かれたときも）
//   - 履歴が無い・口が失敗した・0 件なら空のまま

/** Cookie productLog（古い順の JSON 配列）から、新しい順・重複なしの商品 ID を HISTORY_IDS_MAX 個まで */
export function historyIds(cookie: string): number[] {
  const raw = readCookie(PRODUCT_LOG_COOKIE, cookie)
  if (raw == null) return []
  let arr: unknown
  try {
    arr = JSON.parse(raw)
  } catch {
    return []
  }
  if (!Array.isArray(arr)) return []
  const ids: number[] = []
  for (let i = arr.length - 1; i >= 0 && ids.length < HISTORY_IDS_MAX; i--) {
    const s = String(arr[i])
    if (!/^\d{1,9}$/.test(s)) continue
    const n = Number(s)
    if (n > 0 && !ids.includes(n)) ids.push(n)
  }
  return ids
}

/** 口を叩いて「商品名[型番]」を新しい順に。ids が無ければ叩かない。失敗は [] （投げない） */
export async function fetchHistory(api: string, siteInfoId: number, ids: number[], fetchFn: typeof fetch = fetch): Promise<string[]> {
  if (ids.length === 0) return []
  try {
    const r = await fetchFn(`${api}/history-products?siteInfoId=${siteInfoId}&ids=${ids.join(',')}`, { cache: 'no-store' })
    if (!r.ok) return []
    const j = (await r.json()) as { items?: unknown }
    if (!Array.isArray(j.items)) return []
    return j.items.filter((x): x is string => typeof x === 'string' && x !== '').slice(0, HISTORY_ITEMS_MAX)
  } catch {
    return []
  }
}

/** 欄に入れる文字（1 行 1 品） */
export const historyText = (items: string[]): string => items.join('\n')

export interface HistoryFillerDeps {
  data: Pick<FormData, 'delivery' | 'sample'>
  history?: () => Promise<string[]>
  /** 入力の画面か（確認の画面へ進んだあとに入れ直さない） */
  isEditing: () => boolean
  /** 欄に入れたあと（保存と描き直し） */
  applied: () => void
}

/**
 * お届けの選択が変わるたびに呼ぶ関数を返す。「無料」「有料」で欄が空なら閲覧履歴を入れる。
 * 戻り値の Promise は試験用（入れたら true）。返事を待つ間に書かれた・確認へ進んだ・0 件・失敗なら入れない
 */
export function createHistoryFiller(deps: HistoryFillerDeps): () => Promise<boolean> {
  let loading = false
  const d = deps.data
  const canFill = (): boolean => (d.delivery === 'free' || d.delivery === 'pay') && d.sample.trim() === ''
  return async () => {
    if (!deps.history || loading || !canFill()) return false
    loading = true
    try {
      const items = await deps.history()
      if (items.length === 0 || !canFill() || !deps.isEditing()) return false
      d.sample = historyText(items.slice(0, HISTORY_ITEMS_MAX))
      deps.applied()
      return true
    } catch {
      return false // 空のまま
    } finally {
      loading = false
    }
  }
}

// ---- 入力の再開（localStorage） ------------------------------------------------

export const STORAGE_KEY = 'wu-sample-form'
/** 旧が入力を JSON で残していた Cookie（angular の $cookies）。新が起動したら消す */
export const OLD_COOKIE = 'sampleForm'
/** 残すのは 30 日まで。過ぎたら読まずに消す */
export const DRAFT_TTL_MS = 30 * 24 * 3600 * 1000

export interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

/** 知っている項目の文字列だけを拾う（壊れた値・余計な項目は捨てる） */
export function pickDraft(v: unknown): Draft | null {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return null
  const out: Draft = {}
  let any = false
  for (const k of DRAFT_FIELDS) {
    const x = (v as Record<string, unknown>)[k]
    if (typeof x === 'string') {
      out[k] = x
      any = true
    }
  }
  return any ? out : null
}

/** 残っている入力。無い・壊れている・30 日を過ぎた・storage が使えない → null（過ぎたものと壊れたものは消す） */
export function loadDraft(storage: StorageLike | null, now: number): Draft | null {
  if (!storage) return null
  try {
    const raw = storage.getItem(STORAGE_KEY)
    if (raw === null) return null
    const v = JSON.parse(raw) as { savedAt?: unknown; data?: unknown }
    const savedAt = typeof v?.savedAt === 'number' ? v.savedAt : NaN
    const draft = pickDraft(v?.data)
    if (!(now - savedAt >= 0 && now - savedAt < DRAFT_TTL_MS) || !draft) {
      storage.removeItem(STORAGE_KEY)
      return null
    }
    return draft
  } catch {
    try {
      storage?.removeItem(STORAGE_KEY)
    } catch {
      /* storage が使えない環境。フォームはそのまま動く */
    }
    return null
  }
}

export function saveDraft(storage: StorageLike | null, d: FormData, now: number): void {
  if (!storage) return
  const data: Draft = {}
  for (const k of DRAFT_FIELDS) data[k] = d[k]
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify({ savedAt: now, data }))
  } catch {
    /* 容量切れ・使えない環境。残せないだけで、フォームは動く */
  }
}

export function clearDraft(storage: StorageLike | null): void {
  try {
    storage?.removeItem(STORAGE_KEY)
  } catch {
    /* 同上 */
  }
}

/** 旧の Cookie sampleForm の中身（angular の $cookies.put は encodeURIComponent した JSON）。無い・壊れていれば null */
export function readOldCookie(cookie: string): Draft | null {
  const m = cookie.match('(?:^|; )' + OLD_COOKIE + '=([^;]*)')
  if (!m) return null
  try {
    return pickDraft(JSON.parse(decodeURIComponent(m[1]!)))
  } catch {
    return null
  }
}

/**
 * 旧の Cookie を消すための代入文。angular の $cookies は path に <base href>（無ければ未指定＝ページのディレクトリ）を使うので、
 * 置かれうる path を全部消す（ドメインは未指定＝ホストだけの Cookie）。
 */
export function oldCookieDeletions(pathname: string): string[] {
  const dir = pathname.replace(/\/[^/]*$/, '') || '/'
  const paths = Array.from(new Set(['/', dir, dir + '/', pathname]))
  const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT'
  return [`${OLD_COOKIE}=; ${expired}`, ...paths.map((p) => `${OLD_COOKIE}=; ${expired}; path=${p}`)]
}

/** 開いたときの入力: 初期値 ← 旧の Cookie ← localStorage の順に重ねる（新しい置き場所が勝つ） */
export function restoreFormData(referrer: string, local: Draft | null, cookie: Draft | null): FormData {
  return { ...defaultFormData(referrer), ...(cookie ?? {}), ...(local ?? {}), refUrl: referrer }
}

// ---- 画面の状態 -------------------------------------------------------------

export type PageMode = 'Edit' | 'Confirm' | 'Sending' | 'Complete'

export interface ViewState {
  pageMode: PageMode
  /** 確認へ進もうとしたか（旧の c.submited。エラーの一覧はこれが立ってから出す） */
  submited: boolean
  /** 完了のあとに「戻る」を押した（旧の c.isSendedError） */
  isSendedError: boolean
  /** 送信の失敗の案内（無ければ null） */
  error: string | null
}

export interface ControllerDeps {
  send: () => Promise<SendResult>
  isValid: () => boolean
  /** 画面が移ったときに履歴へ積む（ブラウザの「戻る」で一つ前の画面に戻す） */
  push: (mode: PageMode) => void
  render: (s: ViewState) => void
  /** 送信が済んだとき（入力の再開を消す） */
  onSent?: () => void
}

/** 画面の状態（DOM に依らない）。入力 → 確認 → 送信中 → 完了。二度押し・送信失敗・「戻る」を持つ */
export function createController(deps: ControllerDeps): {
  state: () => ViewState
  toConfirm: () => void
  backToEdit: () => void
  submit: () => Promise<void>
  popstate: (mode: unknown) => void
} {
  let s: ViewState = { pageMode: 'Edit', submited: false, isSendedError: false, error: null }
  const set = (next: Partial<ViewState>): void => {
    s = { ...s, ...next }
    deps.render(s)
  }
  return {
    state: () => s,
    toConfirm: () => {
      if (s.pageMode !== 'Edit') return
      if (!deps.isValid()) {
        set({ submited: true })
        return
      }
      deps.push('Confirm')
      set({ submited: true, pageMode: 'Confirm', error: null })
    },
    backToEdit: () => {
      if (s.pageMode !== 'Confirm') return
      deps.push('Edit')
      set({ pageMode: 'Edit', error: null })
    },
    submit: async () => {
      if (s.pageMode !== 'Confirm') return // 二度押し・送信中・送信後の再送を止める
      set({ pageMode: 'Sending', error: null })
      const r = await deps.send()
      if (r === 'ok') {
        deps.onSent?.()
        deps.push('Complete')
        set({ pageMode: 'Complete', error: null })
      } else {
        // 確認の画面に戻して案内を出す。ボタンはもう一度押せる
        set({ pageMode: 'Confirm', error: TEXT.sendFailed })
      }
    },
    popstate: (mode: unknown) => {
      if (s.pageMode === 'Complete') {
        set({ isSendedError: true }) // 送信済みなので戻れない（旧と同じ案内を出す）
        return
      }
      if (s.pageMode === 'Sending') return
      if (mode === 'Edit') set({ pageMode: 'Edit', error: null })
      else if (mode === 'Confirm' && deps.isValid()) set({ pageMode: 'Confirm', submited: true, error: null })
    },
  }
}

// ---- 表示の式（data-wu-show） ------------------------------------------------

export interface ShowContext {
  pageMode: PageMode
  delivery: string
  isSendedError: boolean
  submited: boolean
  valid: boolean
}

const CMP = /^(c\.pageMode|c\.formData\.delivery)\s*(==|!=)\s*'([^']*)'$/

/** 11 本で使っている表示の式（c. 付きの形）だけを読む。読めない式は undefined（その要素は触らない） */
export function evalShow(expr: string, ctx: ShowContext): boolean | undefined {
  let result = true
  for (const raw of expr.split('&&')) {
    let term = raw.trim()
    let negate = false
    if (term.startsWith('!')) {
      negate = true
      term = term.slice(1).trim()
    }
    let v: boolean
    if (term === 'c.validate()') v = ctx.valid
    else if (term === 'c.submited') v = ctx.submited
    else if (term === 'c.isSendedError') v = ctx.isSendedError
    else {
      const m = CMP.exec(term)
      if (!m) return undefined
      const left = m[1] === 'c.pageMode' ? ctx.pageMode : ctx.delivery
      v = m[2] === '==' ? left === m[3] : left !== m[3]
    }
    result = result && (negate ? !v : v)
  }
  return result
}

// ---- 目印（data-wu-*。Issue #123 / #130） -------------------------------------

/** data-wu-click / data-wu-change の名前 */
export const WU_ACTIONS = ['toConfirm', 'toEdit', 'send', 'zipToAddress', 'addSample'] as const
export type WuAction = (typeof WU_ACTIONS)[number]

type Marked = Pick<Element, 'getAttribute'>

/** data-wu-show の式（「c.」「c.formData.」を落とした形）を c. 付きの形に直す。evalShow はこの形を読む */
export function toNgShow(expr: string): string {
  return expr.replace(/(^|[^\w.'])(validate\(\)|submited|isSendedError|pageMode|delivery)(?![\w(])/g, (_m, pre: string, id: string) =>
    pre + (id === 'delivery' ? 'c.formData.delivery' : `c.${id}`),
  )
}

/** 表示の式（evalShow の読む形）。目印が無ければ null */
export function showOf(el: Marked): string | null {
  const wu = el.getAttribute('data-wu-show')
  return wu === null ? null : toNgShow(wu)
}

/** 入力欄の項目名（data-wu-model="name" → name）。目印が無い・空なら null */
export function modelOf(el: Marked): string | null {
  return (el.getAttribute('data-wu-model') ?? '').trim() || null
}

/** 押したとき・変えたときの動作。目印が無い・知らない名前は null */
export function actionOf(el: Marked, kind: 'click' | 'change'): WuAction | null {
  const name = (el.getAttribute(`data-wu-${kind}`) ?? '').trim()
  return (WU_ACTIONS as readonly string[]).includes(name) ? (name as WuAction) : null
}

/** エラーの一覧の雛形か（data-wu-repeat="errorMessages"） */
export function isErrorRepeat(el: Marked): boolean {
  return (el.getAttribute('data-wu-repeat') ?? '').trim() === 'errorMessages'
}

/** data-wu-text / data-wu-value の項目の値。知らない項目は空 */
function valueOf(d: FormData, field: string): string {
  const v = Object.prototype.hasOwnProperty.call(d, field) ? (d as unknown as Record<string, unknown>)[field] : undefined
  return typeof v === 'string' ? v : ''
}

// ---- DOM -------------------------------------------------------------------

type Field = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

export interface MountOptions {
  siteInfoId: number
  data: FormData
  send: (req: SampleFormRequest) => Promise<SendResult>
  zip: (code: string) => Promise<ZipResult>
  /** 入力が変わったとき（入力の再開に残す） */
  save: (d: FormData) => void
  /** 送信が済んだとき（入力の再開を消す） */
  sent: () => void
  alert: (message: string) => void
  /** 閲覧履歴の「商品名[型番]」（新しい順）。無い店（14・15・16）は渡さない */
  history?: () => Promise<string[]>
}

/** ページに束ねる。戻り値は試験・プレビュー用のコントローラ（ページの作りが想定と違えば null） */
export function mountSampleForm(root: HTMLElement, opts: MountOptions): ReturnType<typeof createController> | null {
  const form = root.querySelector('form') ?? root
  const d = opts.data

  // Enter で form が送られてページが読み直されないように（angular の form は action が無いと送信を止めていた）
  form.addEventListener('submit', (ev) => ev.preventDefault())

  // ハニーポット（人には見えない欄）。埋まっていれば API が黙って捨てる
  const honeypot = document.createElement('input')
  honeypot.type = 'text'
  honeypot.name = 'website'
  honeypot.tabIndex = -1
  honeypot.autocomplete = 'off'
  honeypot.setAttribute('aria-hidden', 'true')
  honeypot.style.cssText = 'position:absolute;left:-9999px;width:1px;height:1px;opacity:0'
  form.appendChild(honeypot)

  // 「ご自分で入力 / 閲覧履歴から入力」の radio は隠したまま。閲覧履歴は「無料」「有料」を選んだときに空の欄へ入れる（Issue #107）
  const marked = (attrs: string[]): HTMLElement[] => Array.from(root.querySelectorAll<HTMLElement>(attrs.map((a) => `[${a}]`).join(',')))
  for (const el of marked(['data-wu-model', 'data-wu-click', 'data-wu-change'])) {
    if (modelOf(el) !== 'sampleTextType' && actionOf(el, 'click') !== 'addSample' && actionOf(el, 'change') !== 'addSample') continue
    ;((el.closest('label') as HTMLElement | null) ?? el).style.display = 'none'
  }

  // エラーの一覧（data-wu-repeat の雛形）
  const repeats = marked(['data-wu-repeat'])
    .filter(isErrorRepeat)
    .map((tpl) => {
      const mark = document.createComment('wu-sample-form')
      tpl.parentNode?.replaceChild(mark, tpl)
      tpl.removeAttribute('data-wu-repeat')
      return { tpl, mark, nodes: [] as Node[] }
    })

  // data-wu-text（中身を項目の値で描き直す）と data-wu-value（value を項目の値に）
  const wuTexts = marked(['data-wu-text']).map((el) => ({ el, field: (el.getAttribute('data-wu-text') ?? '').trim() }))
  const wuValues = marked(['data-wu-value']).map((el) => ({ el, field: (el.getAttribute('data-wu-value') ?? '').trim() }))

  const shows = marked(['data-wu-show']).map((el) => ({ el, expr: showOf(el) ?? '' }))
  const fields = Array.from(root.querySelectorAll<Field>('input, textarea, select'))
    .map((el) => ({ el, field: modelOf(el) as keyof FormData | null }))
    .filter((x): x is { el: Field; field: keyof FormData } => !!x.field && x.field in d)
  const clicks = (action: WuAction): HTMLElement[] => marked(['data-wu-click']).filter((b) => actionOf(b, 'click') === action)
  const sendButtons = clicks('send')

  // 送信の失敗の案内（送信ボタンの下）
  const error = document.createElement('p')
  error.className = 'wu-sample-form-error'
  error.setAttribute('role', 'alert')
  error.style.cssText = 'display:none;color:#c00;margin-top:0.5em'
  const anchor = sendButtons[0]?.parentElement
  if (anchor) anchor.appendChild(error)

  const writeField = (el: Field, value: string): void => {
    if (el instanceof HTMLInputElement && (el.type === 'radio' || el.type === 'checkbox')) el.checked = el.value === value
    else if (el.value !== value) el.value = value
  }

  let view: ViewState = { pageMode: 'Edit', submited: false, isSendedError: false, error: null }
  const render = (source?: Field): void => {
    const messages = validate(d)
    const ctx: ShowContext = {
      pageMode: view.pageMode,
      delivery: d.delivery,
      isSendedError: view.isSendedError,
      submited: view.submited,
      valid: messages.length === 0,
    }
    for (const s of shows) {
      const v = evalShow(s.expr, ctx)
      if (v !== undefined) s.el.style.display = v ? '' : 'none'
    }
    for (const t of wuTexts) t.el.textContent = valueOf(d, t.field)
    for (const v of wuValues) {
      v.el.setAttribute('value', valueOf(d, v.field))
      if (v.el instanceof HTMLInputElement) v.el.value = valueOf(d, v.field)
    }
    for (const f of fields) if (f.el !== source) writeField(f.el, d[f.field])
    for (const r of repeats) {
      for (const n of r.nodes) n.parentNode?.removeChild(n)
      r.nodes = messages.map((m) => {
        const li = r.tpl.cloneNode(true) as Element
        for (const el of [li, ...Array.from(li.querySelectorAll('[data-wu-text]'))]) {
          if (el.hasAttribute('data-wu-text')) el.textContent = (el.getAttribute('data-wu-text') ?? '').trim() === 'errorMessage' ? m : ''
        }
        r.mark.parentNode?.insertBefore(li, r.mark)
        return li
      })
    }
    for (const b of sendButtons) (b as HTMLButtonElement).disabled = view.pageMode !== 'Confirm'
    error.textContent = view.error ?? ''
    error.style.display = view.error ? '' : 'none'
  }

  const fillHistory = createHistoryFiller({
    data: d,
    history: opts.history,
    isEditing: () => view.pageMode === 'Edit',
    applied: () => {
      opts.save(d)
      render()
    },
  })

  for (const f of fields) {
    const onChange = (): void => {
      const el = f.el
      if (el instanceof HTMLInputElement && el.type === 'radio') {
        if (!el.checked) return
      }
      d[f.field] = el.value
      opts.save(d)
      render(el)
      if (f.field === 'delivery') void fillHistory()
    }
    f.el.addEventListener('input', onChange)
    f.el.addEventListener('change', onChange)
  }

  // ブラウザの「戻る」: 今の画面を履歴の state に持つ（旧は一つ前の画面を積んでいて、最初の「戻る」で state が null になり例外で止まった）
  const STATE_KEY = 'wuSampleFormPage'
  const push = (mode: PageMode): void => {
    try {
      history.pushState({ [STATE_KEY]: mode }, '')
    } catch {
      /* 履歴を触れない環境でも画面は進む */
    }
  }
  try {
    const cur = (history.state ?? {}) as Record<string, unknown>
    history.replaceState({ ...cur, [STATE_KEY]: 'Edit' }, '')
  } catch {
    /* 同上 */
  }

  const ctl = createController({
    send: () => opts.send(toRequest(d, opts.siteInfoId, honeypot.value)),
    isValid: () => validate(d).length === 0,
    push,
    onSent: opts.sent,
    render: (s) => {
      const moved = s.pageMode !== view.pageMode
      view = s
      render()
      if (moved && root.getBoundingClientRect().top < 0) root.scrollIntoView({ block: 'start' })
    },
  })
  window.addEventListener('popstate', (ev) => {
    const st = ev.state as Record<string, unknown> | null
    if (st && typeof st === 'object' && STATE_KEY in st) ctl.popstate(st[STATE_KEY])
  })

  const on = (action: WuAction, fn: () => void): void => {
    for (const b of clicks(action)) {
      b.addEventListener('click', (ev) => {
        ev.preventDefault()
        fn()
      })
    }
  }
  on('toConfirm', ctl.toConfirm)
  on('toEdit', ctl.backToEdit)
  on('send', () => void ctl.submit())
  on('zipToAddress', () => {
    void opts.zip(d.postcode).then((r) => {
      if (!r.ok) {
        opts.alert(r.message)
        return
      }
      d.address = r.address
      opts.save(d)
      render()
    })
  })

  render()
  return ctl
}

// ---- 入口 ------------------------------------------------------------------

/**
 * ローカル検証（複製ページ + devserver）のための叩き先の上書き。**localhost / 127.0.0.1 だけ**受ける。
 * ★任意の URL を受けると、店の本物のドメインの上で、入力（名前・住所・メール）を他所へ送らせるリンクが作れてしまう。
 */
export function resolveApiBase(search: string): string {
  return resolveDevApiOverride(search, 'wu_sample_form_api', SAMPLE_FORM_API)
}

function localStorageOrNull(): StorageLike | null {
  try {
    const s = window.localStorage
    return s ?? null
  } catch {
    return null // 使えない環境（読むだけで例外）
  }
}

/** ページの var siteInfoId（無ければ hidden の input[name=siteInfoId]）。対象店でなければ null */
export function readSiteInfoId(win: Record<string, unknown>, hiddenValue: string | null | undefined): number | null {
  const fromVar = Number(win.siteInfoId)
  const id = Number.isInteger(fromVar) && fromVar > 0 ? fromVar : Number(hiddenValue)
  return Number.isInteger(id) && SAMPLE_FORM_SITES.includes(id) ? id : null
}

export default function sampleForm(): void {
  const win = window as unknown as Record<string, unknown>
  if (hasOldEcSiteFormApp(win as { angular?: { module?: (name: string) => unknown } })) return
  const root = document.querySelector<HTMLElement>(APP_SELECTOR)
  if (!root) return
  const siteInfoId = readSiteInfoId(win, root.querySelector<HTMLInputElement>('input[name="siteInfoId"]')?.value)
  if (siteInfoId === null) {
    console.warn('[wu] sample-form: siteInfoId が読めません')
    return
  }

  // 入力の再開: localStorage（30 日まで）。旧の Cookie は中身を引き継いでから消す
  const storage = localStorageOrNull()
  const now = Date.now()
  const local = loadDraft(storage, now)
  const cookie = readOldCookie(document.cookie)
  for (const c of oldCookieDeletions(location.pathname)) document.cookie = c
  const data = restoreFormData(document.referrer, local, cookie)
  if (!local && cookie) saveDraft(storage, data, now)

  const params = new URLSearchParams(location.search)
  const preview = params.get('wu_sample_form_preview')
  const api = resolveApiBase(location.search)
  mountSampleForm(root, {
    siteInfoId,
    data,
    send:
      preview !== null
        ? // fetch しない。本番の行・控えメールを作らない
          () => new Promise((resolve) => setTimeout(() => resolve(preview === 'fail' ? 'failed' : 'ok'), 400))
        : (req) => postForm(api, req, fetch, (reason) => reportClientError(api, siteInfoId, reason)),
    zip: (code) => lookupZip(ZIP_API, code),
    save: (d) => saveDraft(storage, d, Date.now()),
    sent: () => clearDraft(storage),
    alert: (m) => window.alert(m),
    history: HISTORY_SITES.includes(siteInfoId) ? () => fetchHistory(api, siteInfoId, historyIds(document.cookie)) : undefined,
  })
}
